import { FC, useState } from 'react';
import classNames from 'classnames';
import { useIntl, defineMessages } from 'dibs-react-intl';
import Share from 'dibs-icons/exports/legacy/Share';
import Copy from 'dibs-icons/exports/legacy/AnchorLink';
import Pinterest from 'dibs-icons/exports/legacy/Pinterest';
import Envelope from 'dibs-icons/exports/legacy/Envelope';
import CopyLink from 'dibs-copy-text/exports/CopyLink';

import IconWrapper from './components/IconWrapper';
import ContentWrapper from './components/ContentWrapper';
import { trackShareProfile } from '../helpers/trackingHelpers';
import { shareViaPinterest, getShareUri } from '../helpers/shareHelpers';
import { onShareEmail, shareViaShareScreen } from './helpers';
import { SHARE_TRACKING_LABEL } from './constants';

import styles from './ShareWrapper.scss';

type Props = {
    isDesktop: boolean;
    photoUrl: string;
    projectUrl: string;
    firmName: string;
    projectName: string;
    photoPath: string;
};

const messages = defineMessages({
    copied: {
        id: 'dip.photoLiking.shareWrapper.copied',
        defaultMessage: 'Link copied!',
    },
});

const ShareWrapper: FC<Props> = ({
    isDesktop,
    firmName,
    projectName,
    projectUrl,
    photoUrl,
    photoPath,
}) => {
    const intl = useIntl();
    firmName = firmName.trim();
    projectName = projectName.trim();
    const photoShareUri = getShareUri(photoUrl);

    const [socialVisible, setSocialVisible] = useState(false);

    const toggleSocialItems = (): void => {
        setSocialVisible(prevSocialVisible => !prevSocialVisible);
    };

    return (
        <div className={styles.shareWrapper}>
            <ContentWrapper vertical fullOpacity>
                <IconWrapper
                    onClick={() => {
                        if (isDesktop) {
                            toggleSocialItems();
                        } else {
                            shareViaShareScreen({ photoShareUri });
                        }
                    }}
                    active={socialVisible}
                >
                    <Share
                        className={classNames(styles.shareIcon, {
                            [styles.white]: socialVisible,
                        })}
                    />
                </IconWrapper>

                {socialVisible && (
                    <>
                        <IconWrapper
                            onClick={() => {
                                shareViaPinterest({
                                    url: projectUrl,
                                    firmName,
                                    projectName,
                                    photoPath,
                                    intl,
                                });
                                trackShareProfile({
                                    label: SHARE_TRACKING_LABEL,
                                    shareType: 'pinterest',
                                });
                            }}
                        >
                            <Pinterest className={styles.socialIcon} />
                        </IconWrapper>
                        <IconWrapper
                            onClick={() => {
                                onShareEmail({
                                    intl,
                                    firmName,
                                    projectName,
                                    photoPath: photoShareUri,
                                });
                                trackShareProfile({
                                    label: SHARE_TRACKING_LABEL,
                                    shareType: 'email',
                                });
                            }}
                        >
                            <Envelope className={styles.socialIcon} />
                        </IconWrapper>
                        <CopyLink
                            onClick={() => {
                                trackShareProfile({
                                    label: SHARE_TRACKING_LABEL,
                                    shareType: 'copy link',
                                });
                            }}
                            textToCopy={photoShareUri}
                            hideLink
                            confirmationText={intl.formatMessage(messages.copied)}
                            tooltipOptions={{ align: 'center' }}
                            icon={
                                <IconWrapper>
                                    <Copy className={styles.socialIcon} />
                                </IconWrapper>
                            }
                        />
                    </>
                )}
            </ContentWrapper>
        </div>
    );
};

export default ShareWrapper;
